"use client";

import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget. Calls `onToken(token)` when solved (and "" on
 * error/expiry so the form can require a fresh token).
 *
 * Non-production with no `NEXT_PUBLIC_TURNSTILE_SITE_KEY`: renders nothing and
 * emits an empty token — the server `verifyTurnstile` bypasses in non-prod, so
 * local dev/test flows work. Production sets the key and renders the real widget.
 */
const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      theme?: "light" | "dark" | "auto";
    }
  ) => string;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export function Turnstile({ onToken }: { onToken: (token: string) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  // `onToken` is a stable setter (useState) from the parent forms, so depending
  // on it directly is safe and avoids mutating a ref during render.
  useEffect(() => {
    if (!SITE_KEY) {
      onToken(""); // non-prod bypass
      return;
    }

    function render() {
      if (!window.turnstile || !containerRef.current || widgetId.current) return;
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY as string,
        callback: (token: string) => onToken(token),
        "error-callback": () => onToken(""),
        "expired-callback": () => onToken(""),
        theme: "dark",
      });
    }

    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      render();
    }

    return () => {
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* widget already gone */
        }
        widgetId.current = null;
      }
    };
  }, [onToken]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" className="my-1" />;
}
